import type { DataSourcesSchemas } from "api-types";

export const SHOPPING = "Shopping";
export const ANALYTICS = "Analytics";
export const MARKETING = "Marketing";
export const ACCOUNTING = "Accounting";
export const STRIPE_SLUG = "stripe2";
export const FACEBOOK_SLUG = "facebook2";

const PRIMARY_REVENUE_SLUGS = [
  "shopify2",
  "shopify-appstore",
  "amazonrutter",
  "woocommerce2",
  "bigcommercerutter",
  "magento2",
  "prestashop",
  "mockify",
];

const SECONDARY_REVENUE_SLUGS = ["stripe2", "ga"];

export const isPrimaryRevenuePlatform = (slug: string): boolean => PRIMARY_REVENUE_SLUGS.includes(slug);
export const isSecondaryRevenuePlatform = (slug: string): boolean => SECONDARY_REVENUE_SLUGS.includes(slug);

const SHOPPING_ORDERING = {
  shopify2: 0,
  amazonrutter: 1,
  woocommerce2: 2,
  bigcommercerutter: 3,
  magento2: 4,
  prestashop: 5,
  stripe2: 6,
  ga: 7,
};

const MARKETING_ORDERING = {
  google_ads2: 1,
  facebook2: 2,
  tiktok: 3,
};

const SHOPPING_ORDERING_LEGACY = {
  shopify2: 0,
  amazonrutter: 1,
  stripe2: 2,
  woocommerce2: 3,
  bigcommercerutter: 4,
  magento2: 5,
  prestashop: 6,
};

const MARKETING_ORDERING_LEGACY = {
  ga: 0,
  google_ads2: 1,
  facebook2: 2,
  tiktok: 3,
};

const ACCOUNTING_ORDERING = {
  xero: 0,
  quickbooks: 1,
  netsuite: 2,
  codat_accounting: 3,
};

export const KNOWN_UNSUPPORTED_ECOMMERCE = [
  "PrestaShop",
  "Etsy",
  "Shopware",
  "CommentSold",
  "Zalando",
  "Bol.com",
  "Webflow",
  "Wix",
  "Squarespace Commerce",
];
export const CODAT_SUPPORTED_ACCOUNTING = ["Sage", "Exact", "FreeAgent", "FreshBooks", "MYOB", "Zoho"];

// Need to check the connections length because some connectors that only allow one connection are marked as disabled when they are enabled.
const byEnabled = (a: DataSourcesSchemas.ConnectorPropertiesSchema, b: DataSourcesSchemas.ConnectorPropertiesSchema) =>
  (b.isEnabled || b.connections.length > 0 ? 1 : -1) - (a.isEnabled || a.connections.length > 0 ? 1 : -1);

export const filteredAndSortedDataSourcesNext = (
  dataSources: DataSourcesSchemas.ConnectorPropertiesSchema[] | any[]
): FilteredDataSources => {
  const shoppingDataSources = dataSources.filter((dataSource) => dataSource.category === SHOPPING);
  const analyticsDataSources = dataSources.filter((dataSource) => dataSource.category === ANALYTICS); // Google analytics
  const marketingDataSources = dataSources.filter((dataSource) => dataSource.category === MARKETING);
  const accountingDataSources = dataSources.filter((dataSource) => dataSource.category === ACCOUNTING);

  return {
    shoppingDataSources: sortShoppingDataSources([...shoppingDataSources, ...analyticsDataSources]), // Online sales
    analyticsDataSources: [], // TODO: Remove
    marketingDataSources: sortMarketingDataSources(marketingDataSources),
    accountingDataSources: sortAccountingDataSources(accountingDataSources),
    paymentsDataSources: [], // TODO: Remove
  };
};

export interface FilteredDataSources {
  shoppingDataSources: DataSourcesSchemas.ConnectorPropertiesSchema[] | any[];
  analyticsDataSources: DataSourcesSchemas.ConnectorPropertiesSchema[] | any[];
  marketingDataSources: DataSourcesSchemas.ConnectorPropertiesSchema[] | any[];
  accountingDataSources: DataSourcesSchemas.ConnectorPropertiesSchema[] | any[];
  paymentsDataSources?: DataSourcesSchemas.ConnectorPropertiesSchema[] | any[];
}

// TODO: once accountingData uses the multiconnector serialiser we can remove any
export const filterDataSources = (
  dataSources: DataSourcesSchemas.ConnectorPropertiesSchema[] | any[],
  usePaymentsSources = false
): FilteredDataSources => {
  /**
   * TODO: Candidate for the BE to handle
   */
  const supportedShoppingDataSources = (dataSource) => {
    // Payments is not a separate category in the backend, so we need to filter it out here
    // Stripe gets bundled with the shopping connectors under normal circumstances
    if (usePaymentsSources && dataSource.slug === STRIPE_SLUG) {
      return false;
    }

    return dataSource.category === SHOPPING;
  };

  const shoppingDataSources = dataSources.filter(supportedShoppingDataSources);
  const analyticsDataSources = dataSources.filter((dataSource) => dataSource.category === ANALYTICS);
  const marketingDataSources = dataSources.filter((dataSource) => dataSource.category === MARKETING);
  const accountingDataSources = dataSources.filter((dataSource) => dataSource.category === ACCOUNTING);
  const paymentsDataSources = dataSources.filter((dataSource) => usePaymentsSources && dataSource.slug === STRIPE_SLUG);

  return {
    shoppingDataSources,
    analyticsDataSources,
    marketingDataSources,
    accountingDataSources,
    paymentsDataSources,
  };
};

const sortShoppingDataSources = (dataSources: DataSourcesSchemas.ConnectorPropertiesSchema[], legacy = false) => {
  const ordering = legacy ? SHOPPING_ORDERING_LEGACY : SHOPPING_ORDERING;
  const byShoppingOrder = (a, b) => ordering[a.slug] - ordering[b.slug];
  return dataSources.sort(byShoppingOrder).sort(byEnabled);
};

const sortMarketingDataSources = (dataSources: DataSourcesSchemas.ConnectorPropertiesSchema[], legacy = false) => {
  const ordering = legacy ? MARKETING_ORDERING_LEGACY : MARKETING_ORDERING;
  const byMarketingOrder = (a, b) => ordering[a.slug] - ordering[b.slug];
  return dataSources.sort(byMarketingOrder).sort(byEnabled);
};

export const getStoreConnectors = ({ shoppingDataSources }: FilteredDataSources) => {
  return sortShoppingDataSources(shoppingDataSources, true);
};

export const getMarketingAndAnalyticsConnectors = ({
  marketingDataSources,
  analyticsDataSources,
}: FilteredDataSources): DataSourcesSchemas.ConnectorPropertiesSchema[] => {
  return sortMarketingDataSources([...marketingDataSources, ...analyticsDataSources], true);
};

const sortAccountingDataSources = (dataSources: DataSourcesSchemas.ConnectorPropertiesSchema[]) => {
  const byAccountingOrder = (a, b) => ACCOUNTING_ORDERING[a.slug] - ACCOUNTING_ORDERING[b.slug];
  return dataSources.sort(byAccountingOrder).sort(byEnabled);
};

export const getAccountingConnectors = ({
  accountingDataSources,
}: FilteredDataSources): DataSourcesSchemas.ConnectorPropertiesSchema[] => {
  const codatRemovedSources = accountingDataSources.filter((connector) => connector.name !== "Codat");
  return sortAccountingDataSources(codatRemovedSources);
};

/**
 * Used to retrieve the human-readable name from the url param sent when a connection is completed
 */
export const dataSourceSlugToName = (slug) => {
  function capitaliseFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }
  const slugMap = {
    shopify2: "Shopify",
    stripe2: "Stripe",
    woocommerce2: "WooCommerce",
    magento2: "Magento",
    prestashop: "PrestaShop",
    google_ads2: "Google Ads",
    facebook2: "Facebook",
    ga: "Google Analytics",
    amazonrutter: "Amazon",
    bigcommercerutter: "BigCommerce",
    tiktok: "TikTok Ads",
    codat_accounting: "Accounting",
  };
  return slugMap[slug] || capitaliseFirstLetter(slug);
};

export const CODAT_PLATFORM_LOGOS: Record<string, string> = {
  ClearBooks: "/fly-assets/logo-icons/clearbooks.svg",
  Exact: "/fly-assets/logo-icons/exact.svg",
  FreeAgent: "/fly-assets/logo-icons/free-agent.svg",
  FreshBooks: "/fly-assets/logo-icons/freshbooks.svg",
  "IRIS KashFlow": "/fly-assets/logo-icons/iris-kashflow.svg",
  MYOB: "/fly-assets/logo-icons/myob.svg",
  Sage: "/fly-assets/logo-icons/sage.svg",
  Wave: "/fly-assets/logo-icons/wave-financial.svg",
  Zoho: "/fly-assets/logo-icons/zoho.svg",
};
